import { useState, useEffect,useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../context/AuthContext";
import logoImage from "../../assets/das_logo.png";
import "./Login.css";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Shield,
  Star,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  X,
} from "lucide-react";

// Redux actions
import {
  loginUser,
  togglePasswordVisibility,
  toggleForgotModal,
  clearError,
} from "../../store/slices/authSlice";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { login } = useAuth();

  // Local form state
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");

  const passwordInputRef = useRef(null);

  // Redux state
  const { loading, error, showPassword, showForgotModal, isAuthenticated } =
    useSelector((state) => state.auth);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/overview", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!username || !password) {
      return;
    }

    try {
      // Dispatch login action
      const result = await dispatch(loginUser({ username, password })).unwrap();

      // If successful, sync with context and navigate
      if (result.token) {
        login(result.token);

        // Small delay for smooth transition
        setTimeout(() => {
          navigate("/overview", { replace: true });
        }, 100);
      }
    } catch (err) {
      // Error is already handled in Redux
      // Clear password on error
      setPassword("");
    }
  };

  const handleUsernameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevents premature form submission
      passwordInputRef.current?.focus(); // Moves focus to password input
    }
  };

  // Handle forgot password
  const handleForgotPassword = (e) => {
    e.preventDefault();
    dispatch(toggleForgotModal(true));
  };

  // Close forgot password modal
  const closeForgotModal = () => {
    dispatch(toggleForgotModal(false));
  };

  // Toggle password visibility
  const handleTogglePassword = () => {
    dispatch(togglePasswordVisibility());
  };

  return (
    <div className="login-stage">
      {/* LEFT: Branding Section */}
      <section className="login-left">
        <div className="login-headline-wrap">
          <span className="login-eyebrow">
            <span className="login-dot"></span> Enterprise Security Platform
          </span>
          <h1 className="login-headline">
            <span className="login-line">Continuous</span>
            <span className="login-line">Identity</span>
            <span className="login-line">Risk</span>
            <span className="login-line login-accent">Management</span>
          </h1>
          <p className="login-subcopy">
            Advanced threat detection and mitigation — powered by AI, engineered
            for enterprises that can't afford a breach.
          </p>

          <div className="login-meta-row">
            <div className="login-meta">
              <Shield size={16} /> SOC 2 Type II Certified
            </div>
            <div className="login-meta">
              <Star size={16} /> ISO 27001 Compliant
            </div>
            <div className="login-meta">
              <CheckCircle size={16} /> 24/7 Monitoring
            </div>
          </div>
        </div>

        <div className="login-foot">
          © 2026 Deep Algorithms · Fostering AI. Connecting Minds.
        </div>
      </section>

      {/* RIGHT: Login Section */}
      <section className="login-right">
        <form className="login-card" onSubmit={handleSubmit} noValidate>
          <div className="login-logo-row">
            <img src={logoImage} alt="Deep Algorithms" />
          </div>
          <div className="login-welcome">Welcome</div>
          <div className="login-welcome-sub">Sign in to access the platform</div>

          {error && (
            <div className="login-error-banner" role="alert">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="login-field">
            <label className="login-label" htmlFor="username">
              <Mail size={14} />
              Username
            </label>
            <div className="login-input-wrap">
              <input
                className="login-input"
                id="username"
                type="email"
                placeholder="name@example.com"
                value={username}
                onChange={(e) => setUserName(e.target.value)}
                onKeyDown={handleUsernameKeyDown}
                disabled={loading}
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="login-field">
            <label className="login-label" htmlFor="password">
              <Lock size={14} /> Password
            </label>
            <div className="login-input-wrap login-input-wrap--password">
              <input
                className="login-input login-input--password"
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                ref={passwordInputRef}
                disabled={loading}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="login-pw-toggle"
                onClick={handleTogglePassword}
                disabled={loading}
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="login-options">
            <div
              className="login-forgot"
              onClick={handleForgotPassword}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleForgotPassword(e);
                }
              }}
            >
              Forgot password?
            </div>
          </div>

          <button className="login-submit" type="submit" disabled={loading}>
            <span>{loading ? "Authenticating..." : "Sign in securely"}</span>
            {!loading && <ArrowRight size={18} />}
            {loading && <span className="login-spinner"></span>}
          </button>
        </form>
      </section>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <>
          <div
            className="login-modal-overlay"
            onClick={closeForgotModal}
            aria-hidden="true"
          />
          <div
            className="modal-container"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <div className="modal-content-simple">
              <button
                className="modal-close-simple"
                onClick={closeForgotModal}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>

              <h2 className="modal-title-simple" id="modal-title">
                Password Reset
              </h2>

              <p className="modal-message-simple">
                Please contact your administrator to reset your password.
              </p>

              <button className="modal-btn-simple" onClick={closeForgotModal}>
                Okay
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Login;